"use client";

import * as React from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, ListMusic, Play, Shuffle, UserRoundIcon } from "lucide-react";
import { AsyncBoundary, EmptyState } from "@gerama/ui";
import { formatJalaliDate, formatNumber } from "@gerama/utils";
import { usePlayerStore, useSiteAuthStore } from "@gerama/stores";
import type { ArtistOverview } from "@gerama/types";
import { MediaImage as Image } from "@/components/media-image";
import { useCoverColor } from "@/components/detail-hero";
import { browserCoreApi } from "@/lib/api";
import { SongRow } from "@/features/songs/song-row";
import { songToTrack } from "@/features/player/to-track";
import { CardGrid, MediaCard, Shelf } from "@/features/discover/shelf";
import { VideoCard } from "@/features/discover/video-card";
import { ArtistDetailSkeleton } from "./artist-detail-skeleton";
import { ArtistDiscography } from "./artist-discography";
import { ArtistMenu, shuffleArtistSongs } from "./artist-menu";
import { FollowArtistButton, useArtistFollow } from "./follow-artist-button";
import { artistKeys } from "./query-keys";
import { artistPath } from "./artist-path";
import { publicPath } from "@/lib/public-path";

export function ArtistDetail({ id }: { id: number }) {
  const signedIn = !!useSiteAuthStore((s) => s.token);
  const query = useQuery({
    queryKey: artistKeys.overview(id, signedIn),
    queryFn: ({ signal }) => browserCoreApi.artists.overview(id, signal),
    staleTime: 60_000,
  });

  return (
    <AsyncBoundary
      variant="immersive"
      query={query}
      skeleton={<ArtistDetailSkeleton />}
      isEmpty={() => false}
      empty={
        <EmptyState
          variant="immersive"
          icon={<UserRoundIcon className="size-8" />}
          title="هنرمند یافت نشد"
        />
      }
      render={(overview) => <ArtistView overview={overview} />}
    />
  );
}

function ArtistView({ overview }: { overview: ArtistOverview }) {
  const { artist, monthlyPlays, latestSong, topSongs, videos, featuring, me } = overview;
  const color = useCoverColor(artist.coverImage, `artist-${artist.id}`);
  const follow = useArtistFollow(
    artist.id,
    me ? { following: me.following, followerCount: artist.followerCount } : undefined,
  );
  const queue = React.useMemo(() => topSongs.map(songToTrack), [topSongs]);
  const context = { contextLabel: artist.name, contextHref: artistPath(artist) };

  return (
    <article className="space-y-10">
      <header
        className="-mx-4 -mt-6 px-4 pt-10 pb-6 sm:mx-0 sm:mt-0 sm:rounded-xl sm:px-8"
        style={{
          background: `linear-gradient(180deg, ${color} 0%, color-mix(in oklab, ${color} 30%, transparent) 75%, transparent 100%)`,
        }}
      >
        <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-end sm:text-start">
          <div className="bg-muted relative size-44 shrink-0 overflow-hidden rounded-full shadow-[0_10px_50px_rgba(0,0,0,0.55)] sm:size-56 lg:size-64">
            {artist.coverImage ? (
              <Image
                src={artist.coverImage}
                alt={artist.name}
                fill
                priority
                sizes="256px"
                className="object-cover"
              />
            ) : (
              <UserRoundIcon className="text-muted-foreground absolute inset-0 m-auto size-1/3" />
            )}
          </div>
          <div className="min-w-0 space-y-3">
            <p className="flex items-center justify-center gap-1.5 text-sm font-medium sm:justify-start">
              <BadgeCheck className="fill-primary size-5 text-white" />
              هنرمند
            </p>
            <h1 className="text-4xl font-black tracking-tight drop-shadow-md sm:text-6xl lg:text-7xl">
              {artist.name}
            </h1>
            <p className="text-foreground/80 text-sm">
              {formatNumber(follow.followerCount, "fa")} دنبال‌کننده
              <span className="mx-2">·</span>
              {formatNumber(monthlyPlays, "fa")} پخش ماهانه
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3 sm:justify-start">
          <button
            type="button"
            onClick={() => void shuffleArtistSongs(artist.id, artist.name, artist.code)}
            aria-label="پخش تصادفی"
            className="solid-primary text-primary-foreground flex size-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95"
          >
            <Shuffle className="size-6" />
          </button>
          <ArtistMenu
            artistId={artist.id}
            artistCode={artist.code}
            artistName={artist.name}
            suggestLess={!!me?.suggestLess}
          />
          <FollowArtistButton artistId={artist.id} />
        </div>
      </header>

      {(latestSong || topSongs.length > 0) && (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          {latestSong && (
            <section className="space-y-4">
              <h2 className="text-xl font-bold sm:text-2xl">آخرین آهنگ</h2>
              <LatestSongCard song={latestSong} artistName={artist.name} artistId={artist.id} artistCode={artist.code} />
            </section>
          )}
          {topSongs.length > 0 && (
            <Shelf title="برترین آهنگ‌ها" href={artistPath(artist, "/songs")}>
              <div>
                {topSongs.slice(0, 5).map((song, i) => (
                  <SongRow
                    key={song.id}
                    song={song}
                    index={i}
                    queue={queue}
                    {...context}
                    hideArtists={song.artists?.length === 1}
                  />
                ))}
              </div>
            </Shelf>
          )}
        </div>
      )}

      {videos.length > 0 && (
        <Shelf title="نماهنگ‌ها" href={artistPath(artist, "/videos")}>
          <CardGrid columns="video">
            {videos.slice(0, 3).map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                contextLabel={`نماهنگ‌های ${artist.name}`}
                contextHref={artistPath(artist, "/videos")}
              />
            ))}
          </CardGrid>
        </Shelf>
      )}

      {featuring.length > 0 && (
        <Shelf title="همراهی در پلی‌لیست‌ها" href={artistPath(artist, "/featuring")}>
          <CardGrid>
            {featuring.slice(0, 5).map((playlist) => (
              <MediaCard
                key={playlist.id}
                href={`/playlists/${playlist.id}`}
                image={playlist.cover}
                title={playlist.name}
                subtitle={`${formatNumber(playlist.songCount, "fa")} آهنگ`}
                fallbackIcon={<ListMusic className="size-8" />}
              />
            ))}
          </CardGrid>
        </Shelf>
      )}

      <ArtistDiscography artistId={artist.id} />

      {(artist.bioLocal?.fa || artist.galleryImages.length > 0) && (
        <section className="bg-card space-y-4 rounded-xl p-6">
          <h2 className="text-lg font-bold">دربارهٔ {artist.name}</h2>
          {artist.bioLocal?.fa && (
            <p className="text-muted-foreground max-w-3xl leading-7 whitespace-pre-line">
              {artist.bioLocal.fa}
            </p>
          )}
          {artist.galleryImages.length > 0 && (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {artist.galleryImages.map((src, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-lg">
                  <Image
                    src={src}
                    alt={`${artist.name} ${i + 1}`}
                    fill
                    className="object-cover"
                    sizes="20vw"
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </article>
  );
}

function LatestSongCard({
  song,
  artistName,
  artistId,
  artistCode,
}: {
  song: NonNullable<ArtistOverview["latestSong"]>;
  artistName: string;
  artistId: number;
  artistCode?: string | null;
}) {
  const play = () =>
    usePlayerStore
      .getState()
      .playSingle(songToTrack(song), { label: artistName, href: artistPath({ id: artistId, code: artistCode }) });
  return (
    <div className="bg-card group flex items-center gap-4 rounded-xl p-4">
      <div className="bg-muted relative size-28 shrink-0 overflow-hidden rounded-lg shadow-lg shadow-black/40 sm:size-32">
        {song.cover && (
          <Image src={song.cover} alt="" fill sizes="128px" className="object-cover" />
        )}
        <button
          type="button"
          onClick={play}
          aria-label="پخش"
          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Play className="size-9 fill-white text-white" />
        </button>
      </div>
      <div className="min-w-0">
        <Link
          href={publicPath("songs", song)}
          className="block truncate text-lg font-bold hover:underline"
        >
          {song.name}
        </Link>
        <p className="text-muted-foreground text-sm">
          {formatJalaliDate(song.releaseDate ?? song.createdAt)}
        </p>
      </div>
    </div>
  );
}
